import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSidebar } from '../../contexts/SidebarContext';
import Header from '../../components/ui/Header';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LogoUpload from '../../components/LogoUpload';
import { emailService, reviewAutomationService, aiService } from '../../utils/dataServices';
import { uploadAndSetLogo, deleteLogo } from '../../utils/logoService';
import { supabase } from '../../utils/supabase';
import DynamicOptionsManager from './components/DynamicOptionsManager';
import SettingsLayout from './components/SettingsLayout';
import SettingsSidebar from './components/SettingsSidebar';
import ProfileTab from './tabs/ProfileTab';
import BusinessTab from './tabs/BusinessTab';
import AutomationTab from './tabs/AutomationTab';
import BillingTab from './tabs/BillingTab';
import IntegrationsTab from './tabs/IntegrationsTab';

const Settings = () => {
  const navigate = useNavigate();
  const { user, userProfile, updateProfile } = useAuth();
  const { currentOrganization, updateOrganization } = useOrganization();
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();

  const [activeTab, setActiveTab] = useState('account');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Message handler for tab components
  const handleMessage = (type, message) => {
    if (type === 'success') {
      setSuccessMessage(message);
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } else if (type === 'error') {
      setErrorMessage(message);
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };
  const [logoUploading, setLogoUploading] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  
  // Account management state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Review settings state
  const [reviewSettings, setReviewSettings] = useState({
    autoSendReviews: true,
    sendDelay: 24,
    reminderDelay: 72,
    maxReminders: 1, // Fixed to 1 reminder only
    emailEnabled: true,
    businessName: 'ServiceFlow Pro',
    googleReviewUrl: '',
    yelpUrl: '',
    facebookUrl: '',
    businessLogoUrl: '',
    autoSendReminders: true
  });

  // Business settings state
  const [businessSettings, setBusinessSettings] = useState({
    organization_name: '',
    business_name: '',
    google_review_url: '',
    yelp_url: '',
    facebook_url: '',
    business_logo_url: ''
  });

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    replyToEmail: '',
    testEmail: ''
  });

  // Statistics
  const [reviewStats, setReviewStats] = useState({
    total: 0,
    last30Days: 0,
    byType: { initial: 0, reminder: 0 },
    lastSent: null
  });

  const [reminderStats, setReminderStats] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    loadSettings();
    loadStatistics();
    
    // Handle URL query parameters for tab navigation
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const validTabs = ['account', 'business', 'reviews', 'integrations', 'email', 'platforms', 'billing', 'options', 'statistics'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, []);

  useEffect(() => {
    // Update email settings when user profile loads
    if (userProfile) {
      setEmailSettings(prev => ({
        ...prev,
        replyToEmail: userProfile.reply_to_email || userProfile.email || ''
      }));
    } else {
      // Ensure we always have string values, even when userProfile is null
      setEmailSettings(prev => ({
        ...prev,
        replyToEmail: ''
      }));
    }
  }, [userProfile]);

  useEffect(() => {
    // Update business settings when organization loads
    if (currentOrganization) {
      setBusinessSettings({
        organization_name: currentOrganization.name || '',
        business_name: currentOrganization.business_name || currentOrganization.name || '',
        google_review_url: currentOrganization.google_review_url || '',
        yelp_url: currentOrganization.yelp_url || '',
        facebook_url: currentOrganization.facebook_url || '',
        business_logo_url: currentOrganization.logo_url || ''
      });
    } else {
      // Ensure we always have string values, even when organization is null
      setBusinessSettings({
        organization_name: '',
        business_name: '',
        google_review_url: '',
        yelp_url: '',
        facebook_url: '',
        business_logo_url: ''
      });
    }
  }, [currentOrganization]);

  const loadSettings = () => {
    try {
      const settings = reviewAutomationService.getBusinessSettings();
      setReviewSettings(settings);

      // Business settings now come from organization data via useEffect
      // No longer loading from localStorage

      // Load email settings from localStorage and user profile
      const savedEmailSettings = localStorage.getItem('emailSettings');
      const baseEmailSettings = {
        replyToEmail: userProfile?.reply_to_email || userProfile?.email || '',
        testEmail: ''
      };
      
      if (savedEmailSettings) {
        setEmailSettings({ ...baseEmailSettings, ...JSON.parse(savedEmailSettings) });
      } else {
        setEmailSettings(baseEmailSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadStatistics = () => {
    try {
      const stats = reviewAutomationService.getReviewStatistics();
      setReviewStats(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleReviewSettingChange = (field, value) => {
    setReviewSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailSettingChange = (field, value) => {
    setEmailSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBusinessSettingChange = (field, value) => {
    setBusinessSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Save review settings
      const result = reviewAutomationService.saveBusinessSettings(reviewSettings);
      
      if (result.success) {
        // Save business settings to organization
        if (currentOrganization) {
          const orgUpdateResult = await updateOrganization({
            name: businessSettings.organization_name,
            business_name: businessSettings.business_name,
            google_review_url: businessSettings.google_review_url,
            yelp_url: businessSettings.yelp_url,
            facebook_url: businessSettings.facebook_url
            // Note: logo_url is handled separately via logo upload/remove functions
          });
          
          if (!orgUpdateResult.success) {
            setErrorMessage('Failed to update organization settings');
            return;
          }
        }
        
        // Save reply-to email to user profile if it has changed
        if (emailSettings.replyToEmail && emailSettings.replyToEmail !== userProfile?.reply_to_email) {
          const profileResult = await updateProfile({ reply_to_email: emailSettings.replyToEmail });
          
          if (!profileResult.success) {
            setErrorMessage('Failed to update reply-to email');
            return;
          }
        }
        
        // Save other email settings to localStorage
        localStorage.setItem('emailSettings', JSON.stringify(emailSettings));
        
        setSuccessMessage('Settings saved successfully!');
        
        // Clear message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrorMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testEmailService = async () => {
    if (!emailSettings.testEmail) {
      setErrorMessage('Please enter a test email address');
      return;
    }

    setTesting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const result = await reviewAutomationService.testEmailService(emailSettings.testEmail);
      
      if (result.success) {
        setSuccessMessage(`Test email sent successfully to ${emailSettings.testEmail}!`);
      } else {
        setErrorMessage(result.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error testing email service:', error);
      setErrorMessage('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const triggerReminderProcessing = async () => {
    setProcessing(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const result = await reviewAutomationService.triggerReminderProcessing();
      
      if (result.success) {
        setSuccessMessage('Reminder processing triggered successfully!');
      } else {
        setErrorMessage(result.error || 'Failed to trigger reminder processing');
      }
    } catch (error) {
      console.error('Error triggering reminder processing:', error);
      setErrorMessage('Failed to trigger reminder processing');
    } finally {
      setProcessing(false);
    }
  };

  const loadReminderStatistics = async () => {
    setLoadingStats(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const result = await reviewAutomationService.getReminderStatistics();
      
      if (result.success) {
        setReminderStats(result.data);
      } else {
        setErrorMessage(result.error || 'Failed to load reminder statistics');
      }
    } catch (error) {
      console.error('Error loading reminder statistics:', error);
      setErrorMessage('Failed to load reminder statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  // Logo management functions
  const handleLogoUpload = async () => {
    if (!selectedLogoFile || !currentOrganization) {
      return;
    }

    try {
      setLogoUploading(true);
      setErrorMessage('');

      const result = await uploadAndSetLogo(currentOrganization.id, selectedLogoFile);
      
      if (result.success) {
        setSuccessMessage('Logo uploaded successfully!');
        setSelectedLogoFile(null);
        // Update the organization state to reflect the new logo URL
        await updateOrganization({ logo_url: result.logoUrl });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(result.error || 'Failed to upload logo');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      setErrorMessage('An unexpected error occurred while uploading the logo');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!currentOrganization?.logo_url) {
      return;
    }

    try {
      setLogoUploading(true);
      setErrorMessage('');

      const result = await deleteLogo(currentOrganization.id, currentOrganization.logo_url);
      
      if (result.success) {
        setSuccessMessage('Logo removed successfully!');
        setSelectedLogoFile(null);
        // Update the organization state to reflect the removed logo
        await updateOrganization({ logo_url: null });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(result.error || 'Failed to remove logo');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Logo removal error:', error);
      setErrorMessage('An unexpected error occurred while removing the logo');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLogoUploading(false);
    }
  };

  // Account management functions
  const handleDeleteAccount = async () => {
    if (deleteConfirmationText !== 'DELETE MY ACCOUNT') {
      setErrorMessage('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setDeletingAccount(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      console.log('Starting secure account deletion process...');
      
      // Call the secure Edge Function for complete deletion
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          userId: user.id,
          confirmationText: deleteConfirmationText
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to process deletion request');
      }

      if (!data.success) {
        throw new Error(data.error || 'Deletion failed');
      }

      console.log('Deletion successful:', data);
      
      // Show detailed success message
      const { deletedData } = data;
      setSuccessMessage(
        `Account successfully deleted! ` +
        `Profile: ${deletedData.profileDeleted ? 'Removed' : 'Not found'}, ` +
        `Memberships: ${deletedData.membershipsDeleted} removed, ` +
        `Organizations affected: ${deletedData.organizationsAffected}. ` +
        `You will be redirected to login...`
      );

      // Sign out the user (auth user is already deleted by Edge Function)
      // This will clear the local session
      await supabase.auth.signOut();

      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      console.error('Account deletion error:', error);
      setErrorMessage(
        error.message || 'Failed to delete account. Please try again or contact support.'
      );
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeleteConfirmationText('');
    setErrorMessage('');
  };

  const tabs = [
    { id: 'account', label: 'Profile & Account', icon: 'User' },
    { id: 'business', label: 'Business Settings', icon: 'Building' },
    { id: 'reviews', label: 'Review Automation', icon: 'Star' },
    { id: 'integrations', label: 'Platform Integrations', icon: 'Link' },
    { id: 'email', label: 'Email Configuration', icon: 'Mail' },
    { id: 'platforms', label: 'Platform URLs', icon: 'ExternalLink' },
    { id: 'billing', label: 'Billing & Subscription', icon: 'CreditCard' },
    { id: 'options', label: 'Customer Options', icon: 'Settings' },
    { id: 'statistics', label: 'Statistics & Reports', icon: 'BarChart3' }
  ];

  const renderReviewSettings = () => (
    <div className="space-y-6">
      {/* Automation Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Automation Settings
        </h3>
        
        <div className="space-y-4">
          {/* Auto-send toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">
                Automatic Review Requests
              </label>
              <p className="text-sm text-text-secondary">
                Automatically send review requests after service completion
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reviewSettings.autoSendReviews}
                onChange={(e) => handleReviewSettingChange('autoSendReviews', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Email enabled toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">
                Email Notifications
              </label>
              <p className="text-sm text-text-secondary">
                Send review requests via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reviewSettings.emailEnabled}
                onChange={(e) => handleReviewSettingChange('emailEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Send delay */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Send Delay (hours after service completion)
            </label>
            <Input
              type="number"
              min="1"
              max="168"
              value={reviewSettings.sendDelay}
              onChange={(e) => handleReviewSettingChange('sendDelay', parseInt(e.target.value))}
              placeholder="24"
            />
            <p className="text-sm text-text-secondary mt-1">
              Recommended: 24 hours (1-168 hours allowed)
            </p>
          </div>

          {/* Reminder delay */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reminder Delay (hours after first email)
            </label>
            <Input
              type="number"
              min="24"
              max="336"
              value={reviewSettings.reminderDelay}
              onChange={(e) => handleReviewSettingChange('reminderDelay', parseInt(e.target.value))}
              placeholder="72"
            />
            <p className="text-sm text-text-secondary mt-1">
              Recommended: 72 hours (24-336 hours allowed)
            </p>
          </div>

          {/* Max reminders - Fixed to 1 */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Maximum Reminders
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600 font-medium">1 reminder</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              Fixed to send exactly 1 reminder email per review request
            </p>
          </div>
        </div>
      </div>

      {/* Reminder Automation */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Reminder Automation
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Automatically send reminder emails to customers who haven't responded to review requests.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">
                Automatic Reminders
              </label>
              <p className="text-sm text-text-secondary">
                Send reminder emails based on delay settings
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reviewSettings.autoSendReminders}
                onChange={(e) => handleReviewSettingChange('autoSendReminders', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={triggerReminderProcessing}
              disabled={processing}
              variant="outline"
              className="flex-1"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} className="mr-2" />
                  Send Due Reminders
                </>
              )}
            </Button>
            
            <Button
              onClick={loadReminderStatistics}
              disabled={loadingStats}
              variant="ghost"
            >
              {loadingStats ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Icon name="RefreshCw" size={16} />
              )}
            </Button>
          </div>

          {reminderStats && (
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Reminder Statistics ({reminderStats.period})</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Total Requests</p>
                  <p className="text-lg font-semibold text-gray-900">{reminderStats.totalRequests}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-lg font-semibold text-green-600">{reminderStats.completedReviews}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending Reminders</p>
                  <p className="text-lg font-semibold text-yellow-600">{reminderStats.pendingReminders}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completion Rate</p>
                  <p className="text-lg font-semibold text-blue-600">{reminderStats.completionRate}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Business Information Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Business Information</h4>
            <p className="text-blue-800 text-sm mb-2">
              Configure your business information in the Business Settings tab.
            </p>
            <Button
              onClick={() => setActiveTab('business')}
              variant="outline"
              size="sm"
              className="bg-white border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              Go to Business Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      {/* Email Configuration */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Email Configuration
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reply-To Email Address
            </label>
            <Input
              type="email"
              value={emailSettings.replyToEmail}
              onChange={(e) => handleEmailSettingChange('replyToEmail', e.target.value)}
              placeholder="your-email@company.com"
            />
            <p className="text-sm text-text-secondary mt-1">
              When customers reply to review requests, their replies will be sent to this email address. Defaults to your signup email.
            </p>
          </div>
        </div>
      </div>

      {/* SaaS Email Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Email Service</h4>
            <p className="text-blue-800 text-sm mb-2">
              All review request emails are sent through our secure email service. You don't need to configure any email providers.
            </p>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• Professional email delivery</li>
              <li>• High deliverability rates</li>
              <li>• Automatic spam protection</li>
              <li>• Branded email templates</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Test Email */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Test Email Service
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Test Email Address
            </label>
            <Input
              type="email"
              value={emailSettings.testEmail}
              onChange={(e) => handleEmailSettingChange('testEmail', e.target.value)}
              placeholder="test@yourdomain.com"
            />
          </div>

          <Button
            onClick={testEmailService}
            disabled={testing || !emailSettings.testEmail}
            variant="outline"
          >
            {testing ? (
              <>
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                Sending Test...
              </>
            ) : (
              <>
                <Icon name="Send" size={16} className="mr-2" />
                Send Test Email
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Free Tier Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Resend Free Tier</h4>
            <p className="text-blue-800 text-sm mb-2">
              The free tier includes 3,000 emails per month and 100 emails per day.
            </p>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• Perfect for small businesses</li>
              <li>• No setup costs</li>
              <li>• Upgrade as you grow</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlatformSettings = () => (
    <div className="space-y-6">
      {/* External Review Platforms */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          External Review Platforms
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Configure links to external review platforms where customers can post positive reviews (4-5 stars).
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Google Reviews URL
            </label>
            <Input
              type="url"
              value={businessSettings.google_review_url}
              onChange={(e) => handleBusinessSettingChange('google_review_url', e.target.value)}
              placeholder="https://g.page/your-business/review"
            />
            <p className="text-sm text-text-secondary mt-1">
              Get this from your Google Business Profile
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Yelp Review URL
            </label>
            <Input
              type="url"
              value={businessSettings.yelp_url}
              onChange={(e) => handleBusinessSettingChange('yelp_url', e.target.value)}
              placeholder="https://www.yelp.com/writeareview/biz/your-business"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Facebook Review URL
            </label>
            <Input
              type="url"
              value={businessSettings.facebook_url}
              onChange={(e) => handleBusinessSettingChange('facebook_url', e.target.value)}
              placeholder="https://www.facebook.com/your-business/reviews"
            />
          </div>
        </div>
      </div>

      {/* Review Routing Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Smart Review Routing
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="Star" size={20} className="text-red-600" />
            <div>
              <div className="font-medium text-red-900">1-2 Stars</div>
              <div className="text-sm text-red-700">Private feedback form for service recovery</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Icon name="Star" size={20} className="text-yellow-600" />
            <div>
              <div className="font-medium text-yellow-900">3 Stars</div>
              <div className="text-sm text-yellow-700">Internal review only</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Icon name="Star" size={20} className="text-green-600" />
            <div>
              <div className="font-medium text-green-900">4-5 Stars</div>
              <div className="text-sm text-green-700">Offered external platform options with copy functionality</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBusinessSettings = () => (
    <div className="space-y-6">
      {/* Organization Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Organization Information
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          This information appears in review forms and customer communications.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Organization Name *
            </label>
            <Input
              type="text"
              value={businessSettings.organization_name}
              onChange={(e) => handleBusinessSettingChange('organization_name', e.target.value)}
              placeholder="Your Organization Name"
            />
            <p className="text-sm text-text-secondary mt-1">
              This name appears in the review form instead of "Our team"
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Business Name
            </label>
            <Input
              type="text"
              value={businessSettings.business_name}
              onChange={(e) => handleBusinessSettingChange('business_name', e.target.value)}
              placeholder="Your Business Name"
            />
            <p className="text-sm text-text-secondary mt-1">
              Used for AI review improvements and general branding
            </p>
          </div>

          {/* Logo Management Section */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Organization Logo
            </label>
            <div className="space-y-4">
              <LogoUpload
                onFileSelect={setSelectedLogoFile}
                onFileRemove={() => setSelectedLogoFile(null)}
                initialLogoUrl={currentOrganization?.logo_url}
                disabled={logoUploading}
                showConstraints={true}
              />
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                {selectedLogoFile && (
                  <Button
                    onClick={handleLogoUpload}
                    disabled={logoUploading}
                    className="bg-primary text-white hover:bg-primary-600"
                  >
                    {logoUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      'Upload Logo'
                    )}
                  </Button>
                )}
                
                {currentOrganization?.logo_url && !selectedLogoFile && (
                  <Button
                    onClick={handleLogoRemove}
                    disabled={logoUploading}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    {logoUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                        Removing...
                      </>
                    ) : (
                      'Remove Logo'
                    )}
                  </Button>
                )}
              </div>
              
              <p className="text-sm text-text-secondary">
                Your logo will appear in the header next to ServiceFlow branding and can be used in review requests and other customer communications.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Service Information */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Zap" size={20} className="text-green-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-green-900 mb-1">AI Review Enhancement</h4>
            <p className="text-green-800 text-sm mb-2">
              AI-powered review improvement is included with your ServiceFlow Pro subscription.
            </p>
            <ul className="text-green-800 text-sm space-y-1">
              <li>• Automatically suggests review improvements to customers</li>
              <li>• Uses OpenAI's GPT-3.5-turbo for natural language processing</li>
              <li>• Maintains customer's original sentiment and voice</li>
              <li>• No additional configuration required</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );



  const renderStatistics = () => (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-secondary">Total Sent</p>
              <p className="text-2xl font-bold text-foreground">{reviewStats.total}</p>
            </div>
            <Icon name="Mail" size={24} className="text-primary" />
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-secondary">Last 30 Days</p>
              <p className="text-2xl font-bold text-foreground">{reviewStats.last30Days}</p>
            </div>
            <Icon name="Calendar" size={24} className="text-green-600" />
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-secondary">Reminders</p>
              <p className="text-2xl font-bold text-foreground">{reviewStats.byType.reminder}</p>
            </div>
            <Icon name="Bell" size={24} className="text-orange-600" />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Recent Activity
        </h3>
        
        {reviewStats.lastSent ? (
          <div className="space-y-2">
            <p className="text-sm text-text-secondary">
              Last email sent: {new Date(reviewStats.lastSent).toLocaleDateString()} at {new Date(reviewStats.lastSent).toLocaleTimeString()}
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-secondary">Initial requests:</span>
                <span className="ml-2 font-medium text-foreground">{reviewStats.byType.initial}</span>
              </div>
              <div>
                <span className="text-text-secondary">Reminders:</span>
                <span className="ml-2 font-medium text-foreground">{reviewStats.byType.reminder}</span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-text-secondary">No emails sent yet</p>
        )}
      </div>

      {/* Refresh Stats Button */}
      <Button
        onClick={loadStatistics}
        variant="outline"
        className="w-full sm:w-auto"
      >
        <Icon name="RefreshCw" size={16} className="mr-2" />
        Refresh Statistics
      </Button>
    </div>
  );

  const renderAccountManagement = () => (
    <div className="space-y-6">
      {/* Account Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Account Information
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600">{user?.email}</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              Your account email address cannot be changed
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              User ID
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600 font-mono">{user?.id}</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              Your unique user identifier
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Account Created
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-4">
          Danger Zone
        </h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-red-800 mb-2">Delete Account</h4>
            <p className="text-sm text-red-700 mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            
            <div className="bg-red-100 border border-red-300 rounded-md p-4 mb-4">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-red-600 mr-3 mt-0.5" />
                <div>
                  <h5 className="font-medium text-red-800 mb-2">This will permanently delete:</h5>
                  <ul className="text-sm text-red-700 space-y-1 mb-3">
                    <li>• Your user account and authentication credentials</li>
                    <li>• Your user profile and personal settings</li>
                    <li>• All organization memberships</li>
                    <li>• Your email logs and activity history</li>
                  </ul>
                  
                  <h5 className="font-medium text-red-800 mb-2">What will be preserved:</h5>
                  <ul className="text-sm text-red-700 space-y-1 mb-3">
                    <li>• Organization data (for other members)</li>
                    <li>• Customer records (anonymized)</li>
                    <li>• Review data (anonymized)</li>
                    <li>• Business analytics (anonymized)</li>
                  </ul>
                  
                  <div className="bg-red-200 border border-red-400 rounded p-2 text-xs text-red-900">
                    <strong>⚠️ Important:</strong> Organizations with no remaining members will be marked as inactive. This action cannot be reversed.
                  </div>
                </div>
              </div>
            </div>

            {!showDeleteConfirmation ? (
              <Button
                onClick={() => setShowDeleteConfirmation(true)}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Delete Account
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-red-800 mb-2">
                    Type "DELETE MY ACCOUNT" to confirm:
                  </label>
                  <Input
                    type="text"
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="border-red-300 focus:border-red-500 focus:ring-red-500"
                  />
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount || deleteConfirmationText !== 'DELETE MY ACCOUNT'}
                    className="bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300"
                  >
                    {deletingAccount ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Icon name="Trash2" size={16} className="mr-2" />
                        Delete Account
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleCancelDelete}
                    variant="outline"
                    disabled={deletingAccount}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBillingPlaceholder = () => (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="CreditCard" size={48} className="mx-auto text-text-muted mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Billing & Subscription
        </h3>
        <p className="text-text-secondary mb-6 max-w-md mx-auto">
          Manage your subscription, view billing history, and update payment methods. This feature will be available soon with Stripe integration.
        </p>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center space-x-2">
            <Icon name="Clock" size={20} className="text-yellow-600" />
            <span className="text-yellow-800 font-medium">Coming Soon</span>
          </div>
        </div>

        <div className="text-sm text-text-muted">
          <p className="mb-2"><strong>Planned Features:</strong></p>
          <ul className="space-y-1">
            <li>• Subscription management</li>
            <li>• Payment method updates</li>
            <li>• Billing history & invoices</li>
            <li>• Usage analytics</li>
            <li>• Plan upgrades & downgrades</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Settings - ServiceFlow Pro</title>
        <meta name="description" content="Review automation and application settings" />
      </Helmet>

      <Header onSidebarToggle={handleSidebarToggle} sidebarExpanded={sidebarExpanded} />
      <EnhancedSidebar expanded={sidebarExpanded} onToggle={handleSidebarToggle} />

      <main className={`pt-16 transition-smooth ${sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'}`}>
        <EnhancedBreadcrumb />

        <SettingsLayout
          sidebar={
            <SettingsSidebar
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          }
        >
          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'account' && (
              <ProfileTab 
                onMessage={handleMessage}
              />
            )}
            {activeTab === 'business' && (
              <BusinessTab 
                onMessage={handleMessage}
              />
            )}
            {activeTab === 'reviews' && (
              <AutomationTab 
                onMessage={handleMessage}
                onSave={saveSettings}
              />
            )}
            {activeTab === 'integrations' && <IntegrationsTab />}
            {activeTab === 'email' && renderEmailSettings()}
            {activeTab === 'platforms' && renderPlatformSettings()}
            {activeTab === 'billing' && <BillingTab />}
            {activeTab === 'options' && <DynamicOptionsManager />}
            {activeTab === 'statistics' && renderStatistics()}

            {/* Messages */}
            {successMessage && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="CheckCircle" size={16} className="text-green-600 mr-2" />
                  <span className="text-green-700 text-sm">{successMessage}</span>
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={16} className="text-red-600 mr-2" />
                  <span className="text-red-700 text-sm">{errorMessage}</span>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-6 border-t border-border">
              <Button
                onClick={saveSettings}
                disabled={saving}
                variant="primary"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} className="mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </SettingsLayout>
      </main>
    </div>
  );
};

export default Settings;